import type { ReactNode } from "react"
import { X } from "lucide-react"
import {
  CircleAlert,
  CircleCheck,
  Info,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react"
import { toast as sonnerToast } from "sonner"

import { cn } from "@/lib/utils"
import { announce } from "@/lib/announce"

/**
 * Designsystem for toasts i Mimin. Se docs/designsystem-toast.md.
 *
 * Visuelle prinsipper:
 *  - Linear er primær visuell referanse: rolig flate, tynn ramme, myk skygge,
 *    stram typografi og et ikon i en sirkulær ring.
 *  - Raycast er språklig referanse: korte, konsise meldinger («Testmelding
 *    sendt», ikke «Testmeldingen har blitt sendt til …»).
 *  - Vercel er fargereferanse: flaten er alltid nøytral (popover-tokens);
 *    farge finnes KUN i statusikonet og dets ring. Ett unntak: varianten
 *    «brand» (Vercels «Deployment created») er en helfarget flate i
 *    Digitelle-teal, forbeholdt systemmeldingen om ny versjon av Mimin.
 *  - Plassering: alltid nede til høyre (position="bottom-right" på <Toaster>
 *    i admin/notification.tsx — sett aldri posisjon per toast).
 *
 * Anatomi:
 *   ┌────────────────────────────────────────┐
 *   │ (ikon i ring)  Tittel               ✕  │
 *   │                Beskrivelse (valgfri)   │
 *   │                Avvis   Handling        │  ← rad kun når action finnes
 *   └────────────────────────────────────────┘
 *
 * Tokens (alle fra temaet, så lys/mørk følger appen automatisk):
 *  - Flate:       bg-popover text-popover-foreground border rounded-lg shadow-md
 *  - Tittel:      text-sm font-medium
 *  - Beskrivelse: text-sm text-muted-foreground
 *  - Ikonring:    size-8 rounded-full + variantfarge i 10 %/20 % (bg/ring)
 *  - Handlinger:  text-sm font-medium («Avvis» dempet, handling i forgrunn)
 */

export type ToastVariant = "info" | "success" | "warning" | "error" | "brand"

export type ToastAction = {
  label: string
  onClick: () => void
}

// Variant → ikon + eneste fargebruk i systemet (Vercel-nøkternt: én
// aksentfarge per status, resten nøytralt).
const VARIANTS: Record<
  Exclude<ToastVariant, "brand">,
  { Icon: LucideIcon; icon: string; ring: string }
> = {
  info: {
    Icon: Info,
    icon: "text-muted-foreground",
    ring: "bg-muted-foreground/10 ring-muted-foreground/20",
  },
  success: {
    Icon: CircleCheck,
    icon: "text-emerald-600 dark:text-emerald-500",
    ring: "bg-emerald-500/10 ring-emerald-500/20",
  },
  warning: {
    Icon: TriangleAlert,
    icon: "text-amber-600 dark:text-amber-500",
    ring: "bg-amber-500/10 ring-amber-500/20",
  },
  error: {
    Icon: CircleAlert,
    icon: "text-red-600 dark:text-red-500",
    ring: "bg-red-500/10 ring-red-500/20",
  },
}

export type ToastProps = {
  title: ReactNode
  description?: ReactNode
  variant?: ToastVariant
  /** Primærhandling. Når satt vises også «Avvis» ved siden av. */
  action?: ToastAction
  dismissLabel?: string
  /** Lukking initiert av brukeren (✕ eller «Avvis»). */
  onDismiss?: () => void
}

// «brand»: helfarget flate i --primary (Digitelle-teal), som Vercels
// «Deployment created». Ingen ikon og ingen ✕; «Senere» som ren tekst og
// handlingen som hvit knapp, høyrestilt. Brukes for «Ny versjon av Mimin».
const BrandToast = ({
  title,
  description,
  action,
  dismissLabel,
  onDismiss,
}: Omit<ToastProps, "variant">) => (
  <div
    role="status"
    className="pointer-events-auto flex w-full flex-col gap-3 rounded-xl bg-primary p-4 text-primary-foreground shadow-lg sm:w-[356px]"
  >
    <div className="flex flex-col gap-0.5">
      <div className="text-sm font-medium leading-snug [overflow-wrap:anywhere]">
        {title}
      </div>
      {description != null && description !== "" && (
        <div className="text-sm leading-snug text-primary-foreground/80 [overflow-wrap:anywhere]">
          {description}
        </div>
      )}
    </div>
    <div className="flex items-center justify-end gap-4">
      <button
        type="button"
        onClick={onDismiss}
        className="cursor-pointer text-sm font-medium text-primary-foreground/90 transition-colors hover:text-primary-foreground"
      >
        {dismissLabel}
      </button>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="cursor-pointer rounded-md bg-white px-3 py-1.5 text-sm font-medium text-neutral-900 shadow-sm transition-colors hover:bg-neutral-100"
        >
          {action.label}
        </button>
      )}
    </div>
  </div>
)

// Ren presentasjonskomponent – kan rendres hvor som helst (demo, tester).
// I appen fyres den via showToast() under.
export const Toast = ({
  title,
  description,
  variant = "info",
  action,
  dismissLabel = "Avvis",
  onDismiss,
}: ToastProps) => {
  if (variant === "brand") {
    return (
      <BrandToast
        title={title}
        description={description}
        action={action}
        dismissLabel={dismissLabel}
        onDismiss={onDismiss}
      />
    )
  }
  const { Icon, icon, ring } = VARIANTS[variant]
  return (
    <div
      role="status"
      className="pointer-events-auto flex w-full items-start gap-3 rounded-lg border bg-popover p-4 text-popover-foreground shadow-md sm:w-[356px]"
    >
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-full ring-4",
          ring
        )}
      >
        <Icon className={cn("size-4", icon)} aria-hidden />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-1">
        <div className="text-sm font-medium leading-snug [overflow-wrap:anywhere]">
          {title}
        </div>
        {description != null && description !== "" && (
          <div className="text-sm leading-snug text-muted-foreground [overflow-wrap:anywhere]">
            {description}
          </div>
        )}
        {action && (
          <div className="mt-2 flex items-center gap-4">
            <button
              type="button"
              onClick={onDismiss}
              className="cursor-pointer text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {dismissLabel}
            </button>
            <button
              type="button"
              onClick={action.onClick}
              className="cursor-pointer text-sm font-medium text-foreground transition-colors hover:text-muted-foreground"
            >
              {action.label}
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        aria-label="Lukk"
        onClick={onDismiss}
        className="-m-1 cursor-pointer rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}

export type ShowToastOptions = Omit<ToastProps, "title" | "onDismiss"> & {
  /** ms; Infinity = blir stående til den lukkes manuelt. */
  duration?: number
  /**
   * Kalles nøyaktig én gang når toasten lukkes uten at handlingen ble brukt
   * (✕, «Avvis», sveip eller auto-lukking). Klikk på handlingen kaller KUN
   * handlingen – det er slik ra-core sin angre-semantikk forventer det
   * (ellers ville mutasjonen både angres og bekreftes).
   */
  onClose?: () => void
}

// Fyr en toast med designsystemet over. Brukes av admin/notification.tsx
// (useNotify-strømmen); kall den direkte kun utenfor ra-core-flyten.
export const showToast = (title: ReactNode, options: ShowToastOptions = {}) => {
  const { duration, onClose, action, ...toastProps } = options
  // Si meldingen også til skjermlesere; feil og advarsler avbryter.
  // Ikke i tester: live-regionen har samme tekst som toasten, og getByText
  // ville truffet begge. announce() testes for seg.
  const spoken = [title, toastProps.description]
    .filter((part) => typeof part === "string")
    .join(". ")
  if (import.meta.env.MODE !== "test")
    announce(
      spoken,
      toastProps.variant === "error" || toastProps.variant === "warning"
        ? "assertive"
        : "polite"
    )
  // Vakt: onClose skal fyres maks én gang, og ikke ved handlingsklikk.
  let settled = false
  const close = () => {
    if (settled) return
    settled = true
    onClose?.()
  }
  const id = sonnerToast.custom(
    (id) => (
      <Toast
        title={title}
        {...toastProps}
        action={
          action && {
            label: action.label,
            onClick: () => {
              settled = true
              sonnerToast.dismiss(id)
              action.onClick()
            },
          }
        }
        onDismiss={() => {
          close()
          sonnerToast.dismiss(id)
        }}
      />
    ),
    {
      duration,
      // Sikkerhetsnett for sveip/programmatiske lukkinger – vakten over
      // hindrer dobbeltfyring.
      onDismiss: close,
      onAutoClose: close,
    }
  )
  return id
}
